import {
  ChatMessageAttachmentType,
  InstagramMessageType,
  SendInstagramMessageMutationParams,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import {
  ChatInput,
  ChatInputHandle,
  ChatInputParams,
} from "@web-src/modules/chats/components/ChatInput";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useRef, useState } from "react";
import { isAPIError, useToast, useTranslations } from "@jugl-web/utils";
import { useClientSupportPageWithSelectedConversation } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageWithSelectedConversation";
import { useInstagramAccount } from "../../../../providers/InstagramAccountProvider/useInstagramAccount";
import { DisabledChatInput } from "../DisabledChatInput";
import { CRMInvalidUserPopup } from "../CRMInvalidUserPopup";

const MAX_TEXT_LENGTH = 1000;

export const InstagramChatInput: FC<{
  chatId: string;
  isChatExpired?: boolean;
  onMessageSent?: () => void;
}> = ({ chatId, isChatExpired, onMessageSent }) => {
  const { integrationsApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const { t } = useTranslations();
  const { toast } = useToast();
  const {
    selectedConversation,
    incomingInstagramMessages$,
    setIsSendingMessage,
  } = useClientSupportPageWithSelectedConversation();
  const { isConnected, loginInstagramAccount } = useInstagramAccount();

  const [sendInstagramMessage, { isLoading: isSendingInstagramMessage }] =
    integrationsApi.useSendInstagramMessageMutation();
  const [isInvalidUserPopupOpen, setIsInvalidUserPopupOpen] = useState(false);
  const [uploadInstagramMedia, { isLoading: isUploadingInstagramMedia }] =
    integrationsApi.useUploadInstagramMediaMutation();
  const chatInput$ = useRef<ChatInputHandle>(null);

  const isSendingMessage =
    isSendingInstagramMessage || isUploadingInstagramMedia;

  const handleSendMessage: ChatInputParams["onSubmit"] = async ({
    body,
    attachments,
  }) => {
    try {
      setIsSendingMessage(true);

      const attachmentTypeMap: Partial<
        Record<ChatMessageAttachmentType, Exclude<InstagramMessageType, "text">>
      > = {
        [ChatMessageAttachmentType.image]: InstagramMessageType.image,
        [ChatMessageAttachmentType.audio]: InstagramMessageType.audio,
        [ChatMessageAttachmentType.voice]: InstagramMessageType.audio,
        [ChatMessageAttachmentType.video]: InstagramMessageType.video,
      };

      const { audioAttachments, otherAttachments } = attachments?.reduce<{
        audioAttachments: typeof attachments;
        otherAttachments: typeof attachments;
      }>(
        (acc, att) => {
          if (
            att.type === ChatMessageAttachmentType.audio ||
            att.type === ChatMessageAttachmentType.voice
          ) {
            acc.audioAttachments.push(att);
          } else {
            acc.otherAttachments.push(att);
          }
          return acc;
        },
        { audioAttachments: [], otherAttachments: [] }
      ) ?? { audioAttachments: [], otherAttachments: [] };

      if (audioAttachments.length) {
        await Promise.all(
          audioAttachments.map(async (att) => {
            const type = attachmentTypeMap[att.type];
            if (!type) return;

            const { media_id: mediaId, url } = await uploadInstagramMedia({
              entityId,
              file: att.file,
            }).unwrap();

            const payload: SendInstagramMessageMutationParams["data"] = {
              payload: {
                timestamp: Date.now(),
                recipient: { id: selectedConversation.conv_id },
                message: {
                  attachments: [{ media_id: mediaId, type, payload: { url } }],
                },
              },
              msg_type: "attachment",
            };

            const response = await sendInstagramMessage({
              entityId,
              data: payload,
            }).unwrap();

            incomingInstagramMessages$.next(response);
          })
        );
      }

      if (otherAttachments.length) {
        const uploadedOther = await Promise.all(
          otherAttachments.map(async (att) => {
            const type = attachmentTypeMap[att.type];
            if (!type) return null;

            const { media_id: mediaId, url } = await uploadInstagramMedia({
              entityId,
              file: att.file,
            }).unwrap();

            return { media_id: mediaId, type, payload: { url } };
          })
        );

        const filteredOther = uploadedOther.filter(Boolean) as NonNullable<
          (typeof uploadedOther)[number]
        >[];

        if (filteredOther.length > 0) {
          const payload: SendInstagramMessageMutationParams["data"] = {
            payload: {
              timestamp: Date.now(),
              recipient: { id: selectedConversation.conv_id },
              message: { attachments: filteredOther },
            },
            msg_type: "attachment",
          };

          const response = await sendInstagramMessage({
            entityId,
            data: payload,
          }).unwrap();

          incomingInstagramMessages$.next(response);
        }
      }

      const bodyText = body?.trim() || "";
      if (bodyText) {
        const textPayload: SendInstagramMessageMutationParams["data"] = {
          payload: {
            timestamp: Date.now(),
            recipient: { id: selectedConversation.conv_id },
            message: { text: bodyText },
          },
        };

        const textResponse = await sendInstagramMessage({
          entityId,
          data: textPayload,
        }).unwrap();

        incomingInstagramMessages$.next(textResponse);
      }

      if ((attachments?.length ?? 0) > 0 || bodyText) {
        onMessageSent?.();
        chatInput$.current?.clear();
        chatInput$.current?.focus();
      }
    } catch (error) {
      if (isAPIError(error) && error.data.errors === "invalid_user_id") {
        setIsInvalidUserPopupOpen(true);
        return;
      }

      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    } finally {
      setIsSendingMessage(false);
    }
  };

  if (!isConnected) {
    return (
      <DisabledChatInput
        text={t({
          id: "client-support-page.instagram-acoount-is-not-connected-please-reconnect",
          defaultMessage:
            "⚠️ Instagram Account is not connected. Please reconnect to continue using Instagram CRM",
        })}
        buttonProps={{
          text: t({
            id: "common.reconnect",
            defaultMessage: "Reconnect",
          }),
          onClick: loginInstagramAccount,
        }}
      />
    );
  }

  if (isChatExpired) {
    return (
      <DisabledChatInput
        text={t({
          id: "client-support-page.instagram-chat-is-expired",
          defaultMessage:
            "⚠️ Chat is Expired. Clients request didn't receive the response after 24 hours from the last Client message, so it is marked as Expired ⌛",
        })}
      />
    );
  }

  return (
    <>
      <div className="px-8 pb-1.5 pt-[18px]">
        <ChatInput
          id={chatId}
          ref={chatInput$}
          maxTextLenght={MAX_TEXT_LENGTH}
          onSubmit={handleSendMessage}
          isLoading={isSendingMessage}
          toolbarActions={{
            emoji: {
              enabled: true,
            },
            attachments: {
              enabled: !isSendingMessage,
              isCaptionHidden: true,
              isMultiple: true,
              accept: {
                audio: ".m4a,.wav",
                image: ".jpg,.jpeg,.png",
                video: ".mp4,.ogg,.avi,.mov,.webm",
              },
              maxSize: {
                audio: 25,
                image: 8,
                video: 25,
              },
            },
          }}
        />
      </div>
      <CRMInvalidUserPopup
        isOpen={isInvalidUserPopupOpen}
        onRequestClose={() => setIsInvalidUserPopupOpen(false)}
      />
    </>
  );
};
